import clsx from "clsx";
import { useState, type ReactNode } from "react";

import {
  FileManagerBodyContextMenu,
  FileManagerBodyCreateItems,
  FileManagerBodyRefreshItem,
  FileManagerBodySelectionItems,
  FileManagerColumnView,
  FileManagerContainer,
  FileManagerDndProvider,
  FileManagerKeyboardShortcuts,
  FileManagerMenuSeparator,
  FileManagerTreeView,
  useFileManager,
  type FileManagerItem,
  type FileManagerPathAncestor,
  type FileManagerPermissions,
  type LockedFolder,
} from "@/components/core/FileManager";
import { useCrossTenantForSuperAdmin } from "@/hooks/usePlatformView";
import { FileManagerBody } from "@/components/core/FileManager/components/FileManagerBody/FileManagerBody.tsx";
import { FileManagerCard } from "@/components/core/FileManager/components/FileManagerCard/FileManagerCard.tsx";
import {
  FileManagerHeaderActions,
  FileManagerHeaderContainer,
  FileManagerHeaderPath,
  FileManagerSearchInput,
} from "@/components/core/FileManager/components/FileManagerHeader/FileManagerHeader.tsx";
import headerStyles from "@/components/core/FileManager/components/FileManagerHeader/FileManagerHeader.module.scss";
import { FileManagerBodyDeleteItems } from "@/components/core/FileManager/components/FileManagerContextMenu/FileManagerBodyMenuItems.tsx";

import { ensureShareLink } from "@/api/files-hub/fileShares.api";
import { itemShareTarget } from "@/components/features/FileManager/shareTarget.utils";
import { FileManagerModals } from "@/components/features/FileManager/Modals/FileManagerModals.tsx";
import styles from "./SharedFileManager.module.scss";
import {
  FileManagerMountProvider,
  useFileManagerMount,
  type FileManagerMount,
  type RegistryScopedItems,
} from "@/components/features/FileManager/FileManagerMount";
import { useFileOperations } from "@/components/features/FileManager/useFileOperations";
import { FileCard } from "@/components/features/FileManager/FileCard";
import { FileManagerPreviewProvider } from "@/components/features/FileManager/FileManagerPreview";
import { FileViewDropdown } from "@/components/features/FileManager/FileViewDropdown";
import { isApprovedFolderName } from "@/components/features/FileManager/fileTags.utils";
import type { RegistryPagination } from "@/api/file-registry/fileRegistry.types";

export interface SharedFileManagerProps {
  mount: FileManagerMount;
  permissions: FileManagerPermissions;
  title?: string;
  lockedFolders?: LockedFolder[];
  headerLeading?: ReactNode;
  headerAncestors?: FileManagerPathAncestor[];
  /** Fill the parent's height rather than the fixed card height. */
  fillHeight?: boolean;
  headerExtras?: ReactNode;
  renderItem?: (item: FileManagerItem, isOsDragOver?: boolean) => ReactNode;
  renderFooterNote?: (pagination: RegistryPagination | null) => ReactNode;
  urlState?: boolean;
}

function RegistryFooterNote({
  renderFooterNote,
}: {
  renderFooterNote: SharedFileManagerProps["renderFooterNote"];
}) {
  const { items } = useFileManager();
  if (!renderFooterNote) return null;
  return <>{renderFooterNote((items as RegistryScopedItems).registryPagination ?? null)}</>;
}

function SharedFileManagerInner({
  permissions,
  title = "Files",
  lockedFolders,
  headerLeading,
  headerAncestors,
  fillHeight,
  headerExtras,
  renderItem,
  renderFooterNote,
  urlState,
}: Omit<SharedFileManagerProps, "mount">) {
  const [viewMode, setViewMode] = useState<"grid" | "columns">("grid");
  const ops = useFileOperations();
  const { mount, queryScope, resolveDownloadUrl } = useFileManagerMount();
  const isSuperAdmin = useCrossTenantForSuperAdmin();

  const effectivePermissions: FileManagerPermissions = {
    ...permissions,
    canDelete: permissions.canDelete && (isSuperAdmin || permissions.ownerCanDelete),
  };

  const renderFileItem =
    renderItem ??
    ((item: FileManagerItem, isOsDragOver?: boolean) => (
      <FileCard item={item} isOsDragOver={isOsDragOver} />
    ));

  return (
    <div className={clsx(styles.container, fillHeight && styles.containerFill)}>
      <FileManagerContainer
        fetchItems={ops.fetchItems}
        permissions={effectivePermissions}
        lockedFolders={lockedFolders}
        queryScope={queryScope}
        urlState={urlState}
        onResolveDownloadUrl={resolveDownloadUrl}
        onGetShareLink={
          effectivePermissions.canShare
            ? async ({ item }) => (await ensureShareLink(itemShareTarget(mount.type, item))).url
            : undefined
        }
        onMoveItems={effectivePermissions.canEdit ? ops.onMoveItems : undefined}
        onCopyItems={effectivePermissions.canEdit ? ops.onCopyItems : undefined}
        onDeleteFiles={effectivePermissions.canDelete ? ops.onDeleteFiles : undefined}
        onRename={effectivePermissions.canEdit ? ops.onRename : undefined}
        onCreateFolder={effectivePermissions.canCreate ? ops.onCreateFolder : undefined}
        onUpload={effectivePermissions.canCreate ? ops.onUpload : undefined}
        onApprove={effectivePermissions.canApprove ? ops.onApprove : undefined}
        onUpdateTags={effectivePermissions.canEdit ? ops.onUpdateTags : undefined}
        onUpdateNote={effectivePermissions.canEdit ? ops.onUpdateNote : undefined}
      >
        <FileManagerPreviewProvider>
          <FileManagerModals />
          <FileManagerKeyboardShortcuts />
          <FileManagerDndProvider>
            <FileManagerCard subtreeLeft={<FileManagerTreeView fetchItems={ops.fetchItems} />}>
              <FileManagerHeaderContainer>
                {headerLeading}
                <FileManagerHeaderPath rootLabel={title} ancestors={headerAncestors} />
                <div className={headerStyles.actions}>
                  {headerExtras}
                  <FileManagerSearchInput />
                  <FileViewDropdown viewMode={viewMode} onViewChange={setViewMode} />
                  <FileManagerHeaderActions />
                </div>
              </FileManagerHeaderContainer>
              {viewMode === "grid" ? (
                <FileManagerBodyContextMenu
                  menuContent={
                    <>
                      <FileManagerBodySelectionItems />
                      <FileManagerMenuSeparator />
                      <FileManagerBodyCreateItems />
                      <FileManagerBodyDeleteItems />
                      <FileManagerMenuSeparator />
                      <FileManagerBodyRefreshItem />
                    </>
                  }
                >
                  <FileManagerBody renderItem={renderFileItem} />
                </FileManagerBodyContextMenu>
              ) : (
                <FileManagerColumnView
                  fetchItems={ops.fetchItems}
                  hideApproveForColumn={(column) => isApprovedFolderName(column.name)}
                  renderColumn={(content, _colIndex, colFolderId) => (
                    <FileManagerBodyContextMenu
                      menuContent={
                        <>
                          <FileManagerBodySelectionItems />
                          <FileManagerMenuSeparator />
                          <FileManagerBodyCreateItems targetFolderId={colFolderId} />
                          <FileManagerBodyDeleteItems />
                          <FileManagerMenuSeparator />
                          <FileManagerBodyRefreshItem />
                        </>
                      }
                    >
                      {content}
                    </FileManagerBodyContextMenu>
                  )}
                />
              )}
            </FileManagerCard>
          </FileManagerDndProvider>
          <RegistryFooterNote renderFooterNote={renderFooterNote} />
        </FileManagerPreviewProvider>
      </FileManagerContainer>
    </div>
  );
}

export function SharedFileManager({ mount, ...rest }: SharedFileManagerProps) {
  return (
    <FileManagerMountProvider mount={mount}>
      <SharedFileManagerInner {...rest} />
    </FileManagerMountProvider>
  );
}
